import defaultAvatar from "@assets/img/avatars/defaultAvatar.png";
import { Card } from "@components/cards/card";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { ICustomerStatusDTO } from "@hooks/dtos/customer/ICustomerDTO";
import {
  FaCakeCandles,
  FaCircleXmark,
  FaLanguage,
  FaLocationPin,
  FaMapLocation,
  FaPerson,
  FaRegCalendar,
  FaRegClock,
} from "react-icons/fa6";
import {
  FaCheckCircle,
  FaEdit,
  FaShieldAlt,
  FaTransgenderAlt,
  FaUserPlus,
} from "react-icons/fa";
import dayjs from "dayjs";
import { api } from "@service/api";
import {
  ICustomerInfoPublicDTO,
  ICustomerPublicDTO,
} from "@hooks/dtos/user-dto";
import { formatName } from "@hooks/functions/formatName";
import { Button } from "@components/buttons/button";
import { Masks } from "@hooks/functions/Masks/Masks";
import { getGenderNameById } from "@hooks/functions/Customer/gender";
import { getCustomerStatus } from "@hooks/functions/Customer/status";
import { ZuAuthStore } from "@hooks/stores/zustand-auth-store";
import {
  CustomerSocialMediaStatusEnum,
  ICustomerSocialMediaDTO,
} from "@hooks/dtos/customer/ICustomerSocialMediaDTO";
import { SocialMediaBadge } from "@components/social-media";
import { ModalSocialMediaEdit } from "@components/modals/modal-social-media-edit";
import { Link } from "react-router-dom";
import { InputClickToCopy } from "@components/forms/input-click-to-copy";
import { FiCopy } from "react-icons/fi";
import { clickToCopy } from "@hooks/functions/Misc/ClickToCopy";

interface IsAccountLoading {
  info: boolean;
  stats: boolean;
}

interface IModalDTO {
  socialMedia: boolean;
}

interface IProps {
  atSign: string;
}

interface ICustomerStats {
  activeCampaigns: number;
  activeAffiliations: number;
  soldAffiliateTickets: number;
}

const masks = new Masks();
export function Profile({ atSign }: IProps) {
  // --- Default Variables & Functions --- //
  const { customer } = ZuAuthStore();
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();

  const [itsMe, setItsMe] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<IsAccountLoading>({
    info: true,
    stats: true,
  });
  const [isModalOpen, setIsModalOpen] = useState<IModalDTO>({
    socialMedia: false,
  });

  const [profileCustomer, setProfileCustomer] = useState<ICustomerPublicDTO>();
  const [profileCustomerInfo, setProfileCustomerInfo] =
    useState<ICustomerInfoPublicDTO>();
  const [customerSocialMedias, setCustomerSocialMedias] =
    useState<ICustomerSocialMediaDTO[]>();
  const [stats, setStats] = useState<ICustomerStats>({
    activeAffiliations: 0,
    activeCampaigns: 0,
    soldAffiliateTickets: 0,
  });

  const [status, setStatus] = useState<ICustomerStatusDTO>({
    status: "active",
    text: "Ativo",
    title: "Conta Ativa",
    color: "success",
  });

  const handleGetCustomerInfo = async () => {
    try {
      const params = `?atSign=${atSign}`;
      const result = await api.get(`/customer/info/atSign${params}`);
      setProfileCustomer(result.data.customer);
      setProfileCustomerInfo(result.data.customerInfo);
      setCustomerSocialMedias(result.data.socialMedias);

      const _status = getCustomerStatus(result.data.customer.status);
      setStatus(_status);

      setIsLoading((prev) => ({
        ...prev,
        info: false,
      }));
    } catch (error: any) {
      pushToastify({
        message: error?.response?.data?.message,
        type: "error",
        theme: "dark",
      });
    }
  };

  // Fetch customer social media
  const handleLoadCustomerSocialMedias = async () => {
    try {
      const result = await api.get(`/customer/socialMedias`);
      setCustomerSocialMedias(result.data);
    } catch (err: any) {
      pushToastify({
        message:
          err?.response?.data.message ?? "Erro ao carregar redes sociais",
        type: "error",
        theme: "dark",
      });
    } finally {
    }
  };

  const handleGetCustomerStats = async () => {
    try {
      const params = `?atSign=${atSign}`;
      const result = await api.get(`/customer/stats/atSign${params}`);
      setStats(result.data);

      setIsLoading((prev) => ({
        ...prev,
        stats: false,
      }));
    } catch (error: any) {
      pushToastify({
        message: error?.response?.data?.message,
        type: "error",
        theme: "dark",
      });
    }
  };

  const handleSocialMediaClick = async ({
    socialMedia,
  }: {
    socialMedia: ICustomerSocialMediaDTO;
  }) => {
    try {
      const data = { socialMediaId: socialMedia.id };
      const result = await api.post(`/customer/socialMedia/click`, data);
    } catch (error: any) {
      pushToastify({
        message: error?.response?.data?.message,
        type: "error",
        theme: "dark",
      });
    }
  };

  const calculateAge = (birthDate: string): number => {
    const birth = new Date(birthDate);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birth.getDate())
    ) {
      age--;
    }
    return age;
  };

  useEffect(() => {
    handleGetCustomerStats();
    handleGetCustomerInfo();
  }, []);

  useEffect(() => {
    if (profileCustomer) {
      setItsMe(customer?.customer?.id === profileCustomer.id);
    }
  }, [profileCustomer]);

  return (
    <>
      <div className="container-fluid px-0">
        {/* Cover Photo */}
        <div
          className="mb-3 position-relative overflow-hidden"
          style={{
            background:
              "linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #d946ef 100%)",
            height: "180px",
            borderRadius: "16px 16px 0 0",
          }}
        ></div>

        {/* Main Profile Card */}
        <div
          className="card shadow-sm mb-4 position-relative"
          style={{
            borderRadius: "16px",
            marginTop: "-70px",
            zIndex: 10,
          }}
        >
          <div className="card-body p-4">
            <div className="row">
              {/* Avatar Column */}
              <div className="col-auto">
                {isLoading.info || !profileCustomer ? (
                  <Skeleton circle width={120} height={120} />
                ) : (
                  <div className="position-relative">
                    <img
                      src={profileCustomer.avatar || defaultAvatar}
                      alt={profileCustomer.name}
                      className="rounded-circle border border-5 border-white shadow"
                      style={{
                        width: "120px",
                        height: "120px",
                        objectFit: "cover",
                      }}
                    />
                    <span
                      className={`position-absolute bg-${status.color} rounded-circle border border-3 border-white`}
                      style={{
                        bottom: "5px",
                        right: "5px",
                        width: "24px",
                        height: "24px",
                      }}
                      title={status.text}
                    ></span>
                  </div>
                )}
              </div>

              {/* Info Column */}
              <div className="col">
                {isLoading.info || !profileCustomer || !profileCustomerInfo ? (
                  <>
                    <Skeleton width={200} height={32} className="mb-2" />
                    <Skeleton width={150} height={24} />
                  </>
                ) : (
                  <div className="d-flex flex-column">
                    {/* Name & AtSign */}
                    <div className="row">
                      <div className="d-flex flex-column align-items-start">
                        <h1 className="h3 fw-bold mb-1">
                          {formatName(profileCustomer.name)}
                        </h1>
                        <div className="d-flex gap-2">
                          <span className="bg-secondary rounded text-white mb-2 fs-5 px-2 py-1">
                            @{profileCustomer.atSign}
                          </span>
                          <span
                            className="text-muted mb-2 fs-5 px-2 py-1"
                            title={`Clique para copiar url pública do usuário.`}
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              clickToCopy({
                                text: `${window.location.origin}/profile?atsign=${profileCustomer.atSign}`,
                                onSuccess: () =>
                                  pushToastify({
                                    message: "Copiado com sucesso!",
                                    type: "success",
                                    theme: "dark",
                                  }),
                                onError: () =>
                                  pushToastify({
                                    message: "Falha ao copiar.",
                                    type: "error",
                                    theme: "dark",
                                  }),
                              });
                            }}
                          >
                            <FiCopy />
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Location && Since */}
                    <div className="d-flex flex-wrap gap-3 mb-3">
                      <span className="fs-6 text-muted d-flex align-items-center gap-2">
                        <FaLocationPin className="fs-5" />
                        {profileCustomerInfo.cityData?.name},{" "}
                        {profileCustomerInfo.stateData?.iso}
                      </span>

                      <span className="fs-6 text-muted d-flex align-items-center gap-2">
                        <FaRegCalendar className="fs-5" />
                        Desde:{" "}
                        {dayjs(profileCustomer.createdAt).format("DD/MM/YYYY")}
                      </span>
                    </div>

                    {/* Social Medias */}
                    <div className="row">
                      <div className="d-flex flex-wrap justify-content-start gap-2">
                        {customerSocialMedias &&
                          customerSocialMedias?.length > 0 &&
                          customerSocialMedias.map((customerSocialMedia) => {
                            // --- Show just Enables Social Medias --- //
                            if (
                              customerSocialMedia.status !==
                              CustomerSocialMediaStatusEnum.ACTIVE
                            ) {
                              return;
                            }

                            return (
                              <SocialMediaBadge
                                key={customerSocialMedia.id}
                                socialMedia={customerSocialMedia}
                                onClick={(socialMedia) => {
                                  handleSocialMediaClick({ socialMedia });
                                }}
                              />
                            );
                          })}
                        {itsMe && (
                          <span
                            className={`border border-info border-2 rounded d-inline-flex align-items-center fs-6 text-info px-3 py-1`}
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              setIsModalOpen((prev) => ({
                                ...prev,
                                socialMedia: true,
                              }));
                            }}
                          >
                            <FaEdit className="fs-5 me-1" />
                            <span className="fw-semibold">Editar</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Button Column */}
              <div className="col-auto">
                <div className="text-end">
                  {!itsMe && (
                    <Button
                      className="btn btn-primary rounded ms-3"
                      onClick={() => {
                        pushToastify({
                          message: `A adição de amigos estará disponível em breve.`,
                          type: "info",
                          theme: "dark",
                        });
                      }}
                    >
                      <FaUserPlus className="me-1" /> Adicionar
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Statistics Grid */}
        <div className="row g-3 mb-4">
          {isLoading.stats ? (
            <>
              <div key={0} className="col-12">
                <Card>
                  <div className="card-body text-center">
                    <Skeleton height={40} className="" />
                    <Skeleton height={20} className="mt-2" />
                  </div>
                </Card>
              </div>
            </>
          ) : (
            <>
              <div className="col-6 col-lg-6">
                <Link to={`/campaign/default/customer/list/${atSign}`}>
                  <Card className="h-100 border-0 shadow-sm">
                    <div
                      className="card-body text-center p-4"
                      title="Ver campanhas desse Usuário"
                    >
                      <div className="display-6 fw-bold text-success mb-1">
                        {masks.formatMilhar(stats.activeCampaigns)}
                      </div>
                      <div className="text-muted small text-uppercase fw-semibold">
                        Campanha{stats.activeCampaigns > 1 ? "s" : ""} Ativa
                        {stats.activeCampaigns > 1 ? "s" : ""}
                      </div>
                    </div>
                  </Card>
                </Link>
              </div>
              <div className="col-6 col-lg-3">
                <Link to={`/campaign/default/affiliate/list/${atSign}`}>
                  <Card className="h-100 border-0 shadow-sm">
                    <div className="card-body text-center p-4">
                      <div className="display-6 fw-bold text-primary mb-1">
                        {masks.formatMilhar(stats.activeAffiliations)}
                      </div>
                      <div className="text-muted small text-uppercase fw-semibold">
                        Afiliaç{stats.activeAffiliations > 1 ? "ões" : "ão"}{" "}
                        Ativa
                        {stats.activeAffiliations > 1 ? "s" : ""}
                      </div>
                    </div>
                  </Card>
                </Link>
              </div>
              <div className="col-6 col-lg-3">
                <Card className="h-100 border-0 shadow-sm">
                  <div className="card-body text-center p-4">
                    <div
                      className="display-6 fw-bold text-info mb-1"
                      title="Total de ticket(s) vendido(s) como afiliado em Campanhas de parceiros."
                    >
                      {masks.formatMilhar(stats.soldAffiliateTickets)}
                    </div>
                    <div className="text-muted small text-uppercase fw-semibold">
                      Venda{stats.soldAffiliateTickets > 1 ? "s" : ""} c/
                      Afiliado
                    </div>
                  </div>
                </Card>
              </div>
            </>
          )}
        </div>

        {/* Info Cards Row */}
        <div className="row g-4">
          {/* Personal Information */}
          <div className="col-lg-6">
            <Card className="h-100 border-0 shadow-sm">
              <div className="card-body p-4">
                <h5 className="fw-bold mb-4 d-flex align-items-center gap-2">
                  <div
                    className="bg-primary bg-opacity-10 text-white rounded-circle d-flex align-items-center justify-content-center"
                    style={{ width: "40px", height: "40px" }}
                  >
                    <FaPerson className="fs-4" />
                  </div>
                  Informações Pessoais
                </h5>

                {isLoading.info || !profileCustomer || !profileCustomerInfo ? (
                  <Skeleton count={5} height={60} className="mb-3" />
                ) : (
                  <div className="d-flex flex-column gap-3">
                    <div className="d-flex gap-3 p-3 rounded hover-bg-light">
                      <div
                        className="bg-primary bg-opacity-10 text-white rounded d-flex align-items-center justify-content-center flex-shrink-0"
                        style={{ width: "40px", height: "40px" }}
                      >
                        <FaCakeCandles className="fs-5" />
                      </div>
                      <div className="flex-grow-1">
                        <div className="small text-muted text-uppercase fw-semibold mb-1">
                          Idade
                        </div>
                        <div className="fw-semibold">
                          {calculateAge(profileCustomerInfo.birth)} anos
                        </div>
                      </div>
                    </div>

                    <div className="d-flex gap-3 p-3 rounded hover-bg-light">
                      <div
                        className="bg-info bg-opacity-10 text-white rounded d-flex align-items-center justify-content-center flex-shrink-0"
                        style={{ width: "40px", height: "40px" }}
                      >
                        <FaTransgenderAlt className="fs-5" />
                      </div>
                      <div className="flex-grow-1">
                        <div className="small text-muted text-uppercase fw-semibold mb-1">
                          Gênero / Pronomes
                        </div>
                        <div className="fw-semibold">
                          {getGenderNameById(
                            Number(profileCustomerInfo?.gender),
                          )?.name || "Undefined"}{" "}
                          • {profileCustomerInfo.pronoun}
                        </div>
                      </div>
                    </div>

                    <div className="d-flex gap-3 p-3 rounded hover-bg-light">
                      <div
                        className="bg-success bg-opacity-10 text-white rounded d-flex align-items-center justify-content-center flex-shrink-0"
                        style={{ width: "40px", height: "40px" }}
                      >
                        <FaMapLocation className="fs-5" />
                      </div>
                      <div className="flex-grow-1">
                        <div className="small text-muted text-uppercase fw-semibold mb-1">
                          Localização
                        </div>
                        <div className="fw-semibold">
                          {profileCustomerInfo.cityData?.name},{" "}
                          {profileCustomerInfo.stateData?.name} -{" "}
                          {profileCustomerInfo.countryData?.name}
                        </div>
                      </div>
                    </div>

                    <div className="d-flex gap-3 p-3 rounded hover-bg-light">
                      <div
                        className="bg-warning bg-opacity-10 text-white rounded d-flex align-items-center justify-content-center flex-shrink-0"
                        style={{ width: "40px", height: "40px" }}
                      >
                        <FaLanguage className="fs-5" />
                      </div>
                      <div className="flex-grow-1">
                        <div className="small text-muted text-uppercase fw-semibold mb-1">
                          Idioma Preferido
                        </div>
                        <div className="fw-semibold">
                          {profileCustomer.language === "pt-br"
                            ? "Português (Brasil)"
                            : profileCustomer.language}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </div>

          {/* Security & Verification */}
          <div className="col-lg-6">
            <Card className="h-100 border-0 shadow-sm">
              <div className="card-body p-4">
                <h5 className="fw-bold mb-4 d-flex align-items-center gap-2">
                  <div
                    className="bg-success bg-opacity-10 text-white rounded-circle d-flex align-items-center justify-content-center"
                    style={{ width: "40px", height: "40px" }}
                  >
                    <FaShieldAlt className="fs-5" />
                  </div>
                  Segurança & Verificação
                </h5>

                {isLoading.info || !profileCustomer || !profileCustomerInfo ? (
                  <Skeleton count={3} height={60} className="mb-3" />
                ) : (
                  <div className="d-flex flex-column gap-3">
                    <div className="d-flex gap-3 p-3 rounded hover-bg-light">
                      <div
                        className={`${
                          profileCustomerInfo.docsCheck === "y"
                            ? "bg-success bg-opacity-10 text-white"
                            : "bg-danger bg-opacity-10 text-white"
                        } rounded d-flex align-items-center justify-content-center flex-shrink-0`}
                        style={{ width: "40px", height: "40px" }}
                      >
                        {profileCustomerInfo.docsCheck === "y" ? (
                          <FaCheckCircle className="fs-5" />
                        ) : (
                          <FaCircleXmark className="fs-5" />
                        )}
                      </div>
                      <div className="flex-grow-1">
                        <div className="small text-muted text-uppercase fw-semibold mb-1">
                          Documentos
                        </div>
                        <div className="fw-semibold">
                          {profileCustomerInfo.docsCheck === "y"
                            ? "✓ Verificados"
                            : "✗ Pendente"}
                        </div>
                      </div>
                    </div>

                    <div className="d-flex gap-3 p-3 rounded hover-bg-light">
                      <div
                        className={`${
                          profileCustomerInfo.termsCheck === "y"
                            ? "bg-success bg-opacity-10 text-white"
                            : "bg-danger bg-opacity-10 text-white"
                        } rounded d-flex align-items-center justify-content-center flex-shrink-0`}
                        style={{ width: "40px", height: "40px" }}
                      >
                        {profileCustomerInfo.termsCheck === "y" ? (
                          <FaCheckCircle className="fs-5" />
                        ) : (
                          <FaCircleXmark className="fs-5" />
                        )}
                      </div>
                      <div className="flex-grow-1">
                        <div className="small text-muted text-uppercase fw-semibold mb-1">
                          Termos de Uso
                        </div>
                        <div className="fw-semibold">
                          {profileCustomerInfo.termsCheck === "y"
                            ? "✓ Aceitos"
                            : "✗ Pendente"}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="row mt-4">
          <div className="col-12">
            <Card className="border-0 shadow-sm">
              <div className="card-body p-4">
                <h5 className="fw-bold mb-4 d-flex align-items-center gap-2">
                  <div
                    className="bg-info bg-opacity-10 text-white rounded-circle d-flex align-items-center justify-content-center"
                    style={{ width: "40px", height: "40px" }}
                  >
                    <FaRegClock />
                  </div>
                  Atividade Recente
                </h5>

                <div className="text-center py-5">
                  <div className="display-1 text-muted opacity-25 mb-3">📭</div>
                  <p className="text-muted fw-semibold mb-1">
                    Nenhuma atividade recente
                  </p>
                  <small className="text-muted">
                    As novidades do usuário aparecerão aqui
                  </small>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>

      <ModalSocialMediaEdit
        isOpen={isModalOpen.socialMedia}
        onClose={(udpated) => {
          setIsModalOpen((prev) => ({
            ...prev,
            socialMedia: false,
          }));

          if (udpated) {
            handleLoadCustomerSocialMedias();
          }
        }}
      />
    </>
  );
}
